import Workspace from 'iam/models/Workspace'
import { action, computed, makeObservable } from 'mobx'
import InfiniteScrollProvider from 'utils/InfiniteScrollProvider'
import { Subject, Subscription } from 'rxjs'
import { fromStream } from '../../utils/mobx-util'
import { debounceTime, distinctUntilChanged, switchMap } from 'rxjs/operators'
import { GetCurrentUserWorkspaceMemberships } from 'iam/WorkspaceMembershipStore'

export default class WorkspaceSelectorViewModel {
  private searchTextChange = new Subject<string>()

  private subscription = new Subscription()

  constructor(
    private getWorkspaceMemberships: GetCurrentUserWorkspaceMemberships,
  ) {
    makeObservable(this)
  }

  fetchWorkspaces(filter: Record<string, unknown> | undefined) {
    const data = this.getWorkspaceMemberships()
      .filter(
        (wms) =>
          wms.member.hasPermission('MANAGE_WORKSPACE_INVENTORY_ITEMS') &&
          wms.workspace.name
            .toLowerCase()
            .includes(((filter?.searchText || '') as string).toLowerCase()),
      )
      .map((wms) => wms.workspace)
    return { data, total: data.length, cursor: null }
  }

  /**
   * The scroll provider for workspaces.
   */
  scrollProvider = new InfiniteScrollProvider<Workspace>({
    limit: 20,
    fetchItems: async (_params, _, _filter) => this.fetchWorkspaces(_filter),
    startFetchTaskAsPending: true,
  })

  private getSearchText = fromStream('', this.searchTextChange)

  searches$ = this.searchTextChange.pipe(
    debounceTime(500),
    distinctUntilChanged(),
    switchMap((searchText) => this.scrollProvider.fetch({ searchText })),
  )

  activate() {
    this.subscription = this.searches$.subscribe()
  }

  @computed
  get searchText() {
    return this.getSearchText()
  }

  /**
   * Sets the search text.
   *
   * @param text
   */
  @action.bound
  setSearchText(text: string) {
    this.searchTextChange.next(text)
  }

  /**
   * Filtered workspaces.
   */
  @computed
  get filteredWorkspaces() {
    return this.scrollProvider.items
  }

  deactivate() {
    this.subscription.unsubscribe()
  }
}
